import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import {Check} from 'lucide-react';
import {cn} from '../lib/cn';

export type CheckboxSize = 'default' | 'm' | 's';

export interface BaseCheckboxProps extends React.ComponentPropsWithoutRef<
  typeof CheckboxPrimitive.Root
> {
  loading?: boolean;
  readOnly?: boolean;
  size?: CheckboxSize;
}

const checkboxSizeClasses: Record<CheckboxSize, string> = {
  default: 'h-6 w-6',
  m: 'h-5 w-5',
  s: 'h-4 w-4',
};

const checkboxIconSizeClasses: Record<CheckboxSize, string> = {
  default: 'h-5 w-5',
  m: 'h-4 w-4',
  s: 'h-3 w-3',
};

export const BaseCheckbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  BaseCheckboxProps
>(
  (
    {className, loading, onCheckedChange, disabled, readOnly, size = 'default', ...props},
    ref,
  ) => (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        'checkbox__control peer shrink-0 rounded-sm border border-solid border-[var(--checkbox-border)] bg-[var(--checkbox-bg)]',
        'focus-visible:outline-none focus-visible:shadow-chekin-focus',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'aria-busy:cursor-wait aria-busy:opacity-50 aria-readonly:cursor-default aria-readonly:opacity-100',
        'data-[state=checked]:border-[var(--checkbox-checked-border)] data-[state=checked]:bg-[var(--checkbox-checked-bg)] data-[state=checked]:text-[var(--checkbox-check-color)]',
        checkboxSizeClasses[size],
        className,
      )}
      {...props}
      onCheckedChange={!disabled && !readOnly ? onCheckedChange : undefined}
      disabled={disabled}
      aria-busy={loading}
      aria-readonly={readOnly}
    >
      <CheckboxPrimitive.Indicator
        className={cn(
          'checkbox__indicator flex items-center justify-center text-current',
        )}
      >
        <Check className={cn('checkbox__icon', checkboxIconSizeClasses[size])} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  ),
);

BaseCheckbox.displayName = CheckboxPrimitive.Root.displayName;
